import { forwardRef } from 'react';
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { ApiError } from '@lombok/api-client';
import { cn } from '@lombok/ui';

/**
 * Token-backed, RHF-friendly form primitives. They are uncontrolled-by-default
 * (spread `register(...)` onto them) and render a Spanish error message under the
 * control when one is provided. Sizing matches SearchInput (h-11, large mobile
 * tap targets); all colors come from design tokens — no hardcoded brand colors.
 */

const CONTROL_BASE =
  'w-full rounded-md border bg-surface px-3 text-sm text-text-primary placeholder:text-text-muted ' +
  'focus-visible:border-brand-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
  'disabled:cursor-not-allowed disabled:opacity-60';

function controlBorder(hasError?: boolean): string {
  return hasError ? 'border-danger' : 'border-border-strong';
}

interface FieldShellProps {
  label: string;
  htmlFor?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
}

/** Label + control + error/hint wrapper used by every field below. */
export function Field({ label, htmlFor, error, hint, required, children }: FieldShellProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-sm font-medium text-text-primary">
        {label}
        {required ? <span className="ml-0.5 text-danger">*</span> : null}
      </label>
      {children}
      {error ? (
        <p className="text-xs text-danger" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-text-muted">{hint}</p>
      ) : null}
    </div>
  );
}

export interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  hint?: string;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, error, hint, required, id, className, ...rest },
  ref,
) {
  const fieldId = id ?? rest.name;
  return (
    <Field label={label} htmlFor={fieldId} error={error} hint={hint} required={required}>
      <input
        ref={ref}
        id={fieldId}
        className={cn(CONTROL_BASE, 'h-11', controlBorder(Boolean(error)), className)}
        aria-invalid={error ? true : undefined}
        {...rest}
      />
    </Field>
  );
});

export interface TextareaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  error?: string;
  hint?: string;
}

export const TextareaField = forwardRef<HTMLTextAreaElement, TextareaFieldProps>(
  function TextareaField({ label, error, hint, required, id, className, ...rest }, ref) {
    const fieldId = id ?? rest.name;
    return (
      <Field label={label} htmlFor={fieldId} error={error} hint={hint} required={required}>
        <textarea
          ref={ref}
          id={fieldId}
          className={cn(CONTROL_BASE, 'min-h-24 py-2.5', controlBorder(Boolean(error)), className)}
          aria-invalid={error ? true : undefined}
          {...rest}
        />
      </Field>
    );
  },
);

export interface SelectOption {
  value: string;
  label: string;
  /** Shown (e.g. as the current value) but not choosable. */
  disabled?: boolean;
}

export interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  options: readonly SelectOption[];
  error?: string;
  hint?: string;
  /** Optional leading placeholder option (e.g. "Seleccionar…"). */
  placeholder?: string;
}

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  { label, options, error, hint, required, id, className, placeholder, ...rest },
  ref,
) {
  const fieldId = id ?? rest.name;
  return (
    <Field label={label} htmlFor={fieldId} error={error} hint={hint} required={required}>
      <select
        ref={ref}
        id={fieldId}
        className={cn(CONTROL_BASE, 'h-11', controlBorder(Boolean(error)), className)}
        aria-invalid={error ? true : undefined}
        {...rest}
      >
        {placeholder ? (
          <option value="" disabled>
            {placeholder}
          </option>
        ) : null}
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
    </Field>
  );
});

export interface CheckboxFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  error?: string;
}

/** Inline checkbox with a label to its right (used for booleans like `active`). */
export const CheckboxField = forwardRef<HTMLInputElement, CheckboxFieldProps>(
  function CheckboxField({ label, hint, error, id, className, ...rest }, ref) {
    const fieldId = id ?? rest.name;
    return (
      <div className="flex flex-col gap-1.5">
        <label htmlFor={fieldId} className="flex items-center gap-2.5">
          <input
            ref={ref}
            id={fieldId}
            type="checkbox"
            className={cn(
              'h-5 w-5 rounded border-border-strong text-brand-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              className,
            )}
            {...rest}
          />
          <span className="text-sm font-medium text-text-primary">{label}</span>
        </label>
        {error ? (
          <p className="text-xs text-danger" role="alert">
            {error}
          </p>
        ) : hint ? (
          <p className="text-xs text-text-muted">{hint}</p>
        ) : null}
      </div>
    );
  },
);

/** Top-of-form error banner for mutation failures (Spanish message from ApiError). */
export function FormError({ error }: { error: unknown }) {
  if (!error) return null;
  const message =
    error instanceof ApiError
      ? error.message
      : 'No se pudo guardar. Intenta de nuevo.';
  const requestId = error instanceof ApiError ? error.requestId : undefined;
  return (
    <div className="rounded-md border border-danger-soft bg-danger-soft/60 p-3 text-sm text-danger" role="alert">
      <p className="font-medium">{message}</p>
      {requestId ? (
        <p className="mt-0.5 text-xs text-text-muted">Código de referencia: {requestId}</p>
      ) : null}
    </div>
  );
}

/**
 * RHF `setValueAs` helper for optional id/string inputs: turns an empty/blank
 * text value into `undefined` so optional Zod fields (e.g. `idSchema.optional()`)
 * don't fail on an empty string. Use as `register(name, { setValueAs: emptyToUndefined })`.
 */
export function emptyToUndefined(value: unknown): string | undefined {
  if (typeof value !== 'string') return value as string | undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/** Right-aligned (mobile: stacked) action row for forms. */
export function FormActions({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col-reverse gap-2 border-t border-border pt-4 sm:flex-row sm:justify-end">
      {children}
    </div>
  );
}
